import {useEffect, useState} from "react";
import { pageview, button } from "@qoohoo/common/constants/analytics";
import * as Track from "@qoohoo/common/utils/analytics/track";
import Spinner from "@qoohoo/common/library/loader/Spinner";
import httpf from "../../../utils/frontend/httpf";
import toast from "react-hot-toast";
import {MultiCurrency} from "@qoohoo/common/utils/currency";
import useStore from "../../../store/store";
import Modal from "@qoohoo/common/library/modals/Modal";
import BottomSheetModalHOC from "@qoohoo/common/library/modals/BottomSheetModalHOC";

export default function OrderSummaryModal() {
  const modal_name = "order_summary_modal";

  const OrderSummaryModalController = useStore((state) => state.OrderSummaryModalController);
  const {channelDetails, isModalOpened, isContentOpened} = useStore((state) => state.orderSummaryModalState);
  const [data, setData] = useState(false);

  useEffect(() => {
    if (isModalOpened && isContentOpened) {
      Track.customEvent(modal_name, {
        actionType: "modal_open",
        label: modal_name,
        ...getTrackDetails(),
      });
    }
  }, [isModalOpened, isContentOpened]);

  function getTrackDetails() {
    const trackDetails = {
      current_screen: pageview.channel_details,
      modal_name,
      is_paid: true,
      uid: channelDetails.uid,
      name: channelDetails.name,
      type: channelDetails.type,
      recurring_type: channelDetails?.plans?.[0]?.plan_type,
      "creator.uid": channelDetails.creator.uid,
      "creator.full_name": channelDetails.creator.fullname,
      "creator.username": channelDetails.creator.username,
    };

    return trackDetails;
  }

  function handleCloseModal() {
    OrderSummaryModalController().closeModal();

    Track.buttonClick(button.thank_you, getTrackDetails());
  }

  return (
    <Modal isOpen={isModalOpened} closeModal={handleCloseModal}>
      {isModalOpened && (
        <BottomSheetModalHOC>
          <div className="inline-flex items-end justify-center w-full h-full sm:items-center font-nunito">
            <div className="relative bg-[#212327] shadow-xl rounded-t-xl sm:rounded-xl max-w-full w-full sm:max-w-[360px] sm:w-[360px]">
              <img
                src="/assets/icons/close-icon.svg"
                lt="close button"
                onClick={handleCloseModal}
                className="absolute cursor-pointer right-4 top-4"
              />
              <div className="w-full p-6">
                <OrderSummaryContent
                  data={data}
                  setData={setData}
                />
              </div>
            </div>
          </div>
        </BottomSheetModalHOC>
      )}
    </Modal>
  );
}

function OrderSummaryContent({data, setData}) {
  const {
    channelDetails,
    plan_uid,
    coupon_code,
  } = useStore((state) => state.orderSummaryModalState);

  const {uid: channel_uid} = channelDetails;

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchOrderSummary();
  }, [channel_uid, plan_uid, coupon_code]);

  async function fetchOrderSummary() {
    setIsLoading(true);

    try {
      const payload = {
        channel_uid,
        plan_uid,
        coupon_code
      };

      const response = await httpf.post("/api/order-summary", payload);

      const {data} = response?.data || null;

      setData(data);
    } catch(err) {
      console.log(err);
      toast.error("Some network error! Order summary cannot be fetched this time.");
    }
    finally {
      setIsLoading(false);
    }
  }

  let content;
  if (isLoading) {
    content = (
      <div className="mt-8 flex items-center justify-center">
        <Spinner size="md" />
      </div>
    )
  } else if (data) {
    content = (
      <div className="mt-6 text-sm">
        <div className="flex flex-row justify-between	py-3">
          <span>Price</span>
          <span>{MultiCurrency.formatAmountWithSymbol(data.subtotal_amount, data.currency)}</span>
        </div>

        {
          (!!data.discount_amount || !data.tax_details.is_tax_inclusive) && (
            <div className="border-dashed border-t border-[#333A3F]"></div>
          )
        }

        {
          !!data.discount_amount && (
            <div className="flex flex-row justify-between	py-3 text-[#4AFF7D]">
              <span>Discount</span>
              <span>
                - {MultiCurrency.formatAmountWithSymbol(data.discount_amount, data.currency)}
              </span>
            </div>
          )
        }
        {
          !data.tax_details.is_tax_inclusive && (
            <div className="flex flex-row justify-between	py-3">
              <span>Tax ({`${data.tax_details.tax_percentage}% ${data.tax_details.tax_label || data.tax_details.tax_type}`})</span>
              <span>{MultiCurrency.formatAmountWithSymbol(data.tax_details.tax_amount, data.currency)}</span>
            </div>
          )
        }

        {
          data.convenience_fee ? (
            <div className="flex flex-row justify-between	py-3">
              <span>Convenience Fee</span>
              <span>{MultiCurrency.formatAmountWithSymbol(data.convenience_fee, data.currency)}</span>
            </div>
          ) : null
        }

        <div className="border-t border-[#333A3F]"></div>

        <div className="flex font-semibold text-base flex-row justify-between	py-3">
          <span>Total Price</span>
          <span>{MultiCurrency.formatAmountWithSymbol(data.total_amount, data.currency)}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="font-semibold text-xl text-white text-left">
        Order summary
      </div>

      <div className="min-h-[140px]">
        {content}
      </div>
    </>
  );
}